export type Meal = 'NONE' | 'SNACK' | 'MEAL';
export type Tips = 'UNKNOWN' | 'NONE' | 'KEPT' | 'SHARED';
export type Phase = 'BEFORE' | 'DURING' | 'AFTER';

export const MEAL_LABELS: Record<Meal, string> = { NONE: 'Pas de repas fourni', SNACK: 'Collation fournie', MEAL: 'Repas fourni' };
export const TIPS_LABELS: Record<Tips, string> = { UNKNOWN: 'Non précisé', NONE: 'Pas de pourboires', KEPT: 'Pourboires conservés par chacun', SHARED: 'Pourboires partagés entre l’équipe' };
export const PHASE_LABELS: Record<Phase, string> = { BEFORE: 'Avant le service', DURING: 'Pendant le service', AFTER: 'Après le service' };
export const DRESS_SUGGESTIONS = ['Tenue noire', 'Chemise blanche, pantalon noir', 'Tablier fourni', 'Chaussures fermées antidérapantes', 'Cheveux attachés'];

// Ce que renvoie l'API (voir server/briefing.ts, loadBriefing).
export interface BriefingView {
  dressCode: string | null;
  meal: Meal;
  breakMinutes: number;
  breakPaid: boolean;
  tips: Tips;
  parkingInfo: string | null;
  equipmentNotes: string | null;
  notes: string | null;
  briefingMinutesBefore: number | null;
  updatedAt: string | null;
  schedule: { id: string; offsetMin: number; durationMin: number | null; label: string }[];
  checklist: {
    id: string;
    phase: Phase;
    label: string;
    required: boolean;
    checked?: boolean;
    checkedAt?: string | null;
    checks?: { extraId: string; firstName: string; checkedAt: string }[];
  }[];
  private: { address: string; arrivalInstructions: string | null; accessCode: string | null; contactName: string | null } | null;
}

// Ce que manipule le formulaire : des textes, et des horaires « HH:MM » plutôt que des décalages en minutes.
export interface BriefingDraft {
  dressCode: string;
  meal: Meal;
  breakMinutes: string;
  breakPaid: boolean;
  tips: Tips;
  parkingInfo: string;
  equipmentNotes: string;
  notes: string;
  briefingMinutesBefore: string;
  arrivalInstructions: string;
  accessCode: string;
  contactName: string;
  schedule: { id?: string; time: string; durationMin: string; label: string }[];
  checklist: { id?: string; phase: Phase; label: string; required: boolean }[];
}

export const EMPTY_DRAFT: BriefingDraft = {
  dressCode: '', meal: 'NONE', breakMinutes: '', breakPaid: false, tips: 'UNKNOWN', parkingInfo: '', equipmentNotes: '', notes: '', briefingMinutesBefore: '',
  arrivalInstructions: '', accessCode: '', contactName: '', schedule: [], checklist: [],
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Heure « HH:MM » d'un instant, dans le fuseau du navigateur (celui avec lequel les horaires sont saisis). */
export const clockOf = (iso: string | Date) => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Heure d'un point du planning : début de mission + décalage. */
export const clockAt = (startsAt: string, offsetMin: number) => clockOf(new Date(new Date(startsAt).getTime() + offsetMin * 60_000));

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** Décalage (en minutes) d'une heure « HH:MM » par rapport au début : une heure plus tôt que le début tombe le lendemain (service qui passe minuit). */
export function offsetFor(startClock: string, itemClock: string): number {
  const diff = minutesOf(itemClock) - minutesOf(startClock);
  return diff >= 0 ? diff : diff + 24 * 60;
}

export function durationLabel(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} h${m ? ` ${pad(m)}` : ''}` : `${m} min`;
}

type TemplateBriefing = Partial<Omit<BriefingView, 'private' | 'schedule' | 'checklist' | 'updatedAt'>> & {
  arrivalInstructions?: string | null; accessCode?: string | null; contactName?: string | null;
  schedule?: { id?: string; offsetMin: number; durationMin: number | null; label: string }[];
  checklist?: { id?: string; phase: Phase; label: string; required: boolean }[];
};

/** Brouillon à partir d'une fiche existante ou d'un modèle ; `startClock` (HH:MM) sert à afficher les horaires du planning. */
export function draftFrom(b: TemplateBriefing | null | undefined, startClock: string, priv?: BriefingView['private']): BriefingDraft {
  if (!b) return EMPTY_DRAFT;
  const p = priv ?? null;
  const pick = (own: string | null | undefined, fromPrivate: string | null | undefined) => (own ?? fromPrivate ?? '');
  return {
    dressCode: b.dressCode ?? '',
    meal: b.meal ?? 'NONE',
    breakMinutes: b.breakMinutes ? String(b.breakMinutes) : '',
    breakPaid: b.breakPaid ?? false,
    tips: b.tips ?? 'UNKNOWN',
    parkingInfo: b.parkingInfo ?? '',
    equipmentNotes: b.equipmentNotes ?? '',
    notes: b.notes ?? '',
    briefingMinutesBefore: b.briefingMinutesBefore != null ? String(b.briefingMinutesBefore) : '',
    arrivalInstructions: pick(b.arrivalInstructions, p?.arrivalInstructions),
    accessCode: pick(b.accessCode, p?.accessCode),
    contactName: pick(b.contactName, p?.contactName),
    schedule: (b.schedule ?? []).map((s) => ({
      id: s.id,
      time: `${pad(Math.floor(((minutesOf(startClock) + s.offsetMin) % 1440) / 60))}:${pad((minutesOf(startClock) + s.offsetMin) % 60)}`,
      durationMin: s.durationMin ? String(s.durationMin) : '',
      label: s.label,
    })),
    checklist: (b.checklist ?? []).map((c) => ({ id: c.id, phase: c.phase, label: c.label, required: c.required })),
  };
}

/** Corps de requête de l'API ; les lignes vides sont ignorées. */
export function briefingPayload(d: BriefingDraft, startClock: string) {
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  return {
    dressCode: d.dressCode,
    meal: d.meal,
    breakMinutes: num(d.breakMinutes),
    breakPaid: d.breakPaid,
    tips: d.tips,
    parkingInfo: d.parkingInfo,
    equipmentNotes: d.equipmentNotes,
    notes: d.notes,
    briefingMinutesBefore: num(d.briefingMinutesBefore),
    arrivalInstructions: d.arrivalInstructions,
    accessCode: d.accessCode,
    contactName: d.contactName,
    schedule: d.schedule.filter((s) => s.label.trim() && s.time).map((s) => ({ id: s.id, offsetMin: offsetFor(startClock, s.time), durationMin: num(s.durationMin), label: s.label })),
    checklist: d.checklist.filter((c) => c.label.trim()).map((c) => ({ id: c.id, phase: c.phase, label: c.label, required: c.required })),
  };
}

export interface MissionTemplate {
  id: string;
  name: string;
  createdAt: string;
  data: {
    title: string;
    description: string | null;
    hourlyRateCents: number;
    slotsCount: number;
    coversCount: number | null;
    teamMode: 'ALONE' | 'TEAM' | null;
    requirements: { itemId: string; importance: 'REQUIRED' | 'PREFERRED'; minLevel: string | null }[];
    briefing: TemplateBriefing;
  };
}
